import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, Calendar, Upload, Loader2, Eye, Check, ChevronsUpDown, Search } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const API = "/api";
const empty = {
  name: "",
  tagline: "",
  bio: "",
  specialization: "",
  experience_years: 0,
  teaching_style: "",
  education: "",
  card_color: "blue",
  display_order: 100,
  is_active: true,
  is_trial_eligible: true,
  user_id: "",
  zoom_link: "",
  whatsapp: "",
  email: "",
};

export default function AdminTutorProfiles() {
  const { getAuthHeader } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [tutorUsers, setTutorUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=new, else profile object
  const [form, setForm] = useState(empty);
  const [savings, setSavings] = useState(false);
  const [availabilityFor, setAvailabilityFor] = useState(null); // profile object
  const [availDays, setAvailDays] = useState([]);
  const [savingAvail, setSavingAvail] = useState(false);
  const [tutorPickerOpen, setTutorPickerOpen] = useState(false);

  const auth = { headers: getAuthHeader() };

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        axios.get(`${API}/tutors/admin/profiles`, auth),
        axios.get(`${API}/tutors/admin/tutor-users`, auth),
      ]);
      setProfiles(p.data.items || []);
      setTutorUsers(t.data.items || []);
    } catch (e) {
      toast.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setEditorOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error("Name is required");
      return;
    }
    setSavings(true);
    try {
      const payload = { ...form };
      // Coerce numbers
      payload.experience_years = payload.experience_years ? parseInt(payload.experience_years) : null;
      payload.display_order = parseInt(payload.display_order || 100);
      if (editing) {
        await axios.put(`${API}/tutors/admin/profiles/${editing.id}`, payload, auth);
        toast.success("Profile updated");
      } else {
        await axios.post(`${API}/tutors/admin/profiles`, payload, auth);
        toast.success("Profile created");
      }
      setEditorOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavings(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete profile "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/tutors/admin/profiles/${p.id}`, auth);
      toast.success("Deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handlePhotoUpload = async (profileId, file) => {
    const fd = new FormData();
    fd.append("photo", file);
    try {
      await axios.post(`${API}/tutors/admin/profiles/${profileId}/photo`, fd, {
        headers: { ...getAuthHeader() },
      });
      toast.success("Photo uploaded");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const openAvailability = async (p) => {
    if (!p.user_id) {
      toast.error("Link a Tutor User account first (edit profile)");
      return;
    }
    setAvailabilityFor(p);
    try {
      const r = await axios.get(`${API}/tutors/admin/availability/${p.user_id}`, auth);
      setAvailDays(r.data.days || []);
    } catch {
      setAvailDays([]);
    }
  };

  const saveAvailability = async () => {
    if (!availabilityFor) return;
    setSavingAvail(true);
    try {
      await axios.put(
        `${API}/tutors/admin/availability/${availabilityFor.user_id}`,
        { days: availDays },
        auth
      );
      toast.success("Availability saved");
      setAvailabilityFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingAvail(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="admin-tutor-profiles-title">
            Tutor Profiles
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage the public "Our Teaching Experts" cards and weekly availability.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm("Enable the pink 'Book a Free Trial Class' banner on ALL active tutor profiles?")) return;
              try {
                const r = await axios.post(
                  `${API}/tutors/admin/profiles/bulk-trial-eligible`,
                  { is_trial_eligible: true, only_active: true },
                  { headers: getAuthHeader() }
                );
                toast.success(`Updated ${r.data.updated} of ${r.data.matched} active profile(s)`);
                refresh();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Bulk update failed");
              }
            }}
            className="border-pink-300 text-pink-700 hover:bg-pink-50"
            data-testid="bulk-enable-trial-btn"
          >
            ✨ Enable Trial Banner for All Tutors
          </Button>
          <Button variant="outline" asChild>
            <Link to="/tutors" target="_blank" data-testid="view-public-page-btn">
              <Eye className="w-4 h-4 mr-2" /> View Public Page
            </Link>
          </Button>
          <Button onClick={openNew} data-testid="new-tutor-profile-btn">
            <Plus className="w-4 h-4 mr-2" /> New Profile
          </Button>
        </div>
      </div>

      {!loading && profiles.filter((p) => p.link_status === "broken" || p.link_status === "wrong_role").length > 0 && (
        <Card className="border-rose-300 bg-rose-50" data-testid="broken-link-banner">
          <CardContent className="pt-5 pb-5 flex items-start gap-3">
            <span className="text-2xl shrink-0">⚠️</span>
            <div className="text-sm text-rose-900">
              <strong>
                {profiles.filter((p) => p.link_status === "broken" || p.link_status === "wrong_role").length} profile(s)
                {" "}point to a deleted/invalid user account.
              </strong>{" "}
              Their public schedule cannot show real bookings until you re-link them to the correct
              tutor account. Click <em>Edit</em> on any "Broken link" card and pick the right tutor
              from the dropdown — all existing bookings will auto-migrate.
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((p) => (
            <Card key={p.id} data-testid={`admin-tutor-card-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-20 h-20 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl text-slate-400">
                        {p.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 truncate">{p.name}</h3>
                    <p className="text-xs text-slate-500 truncate">
                      {p.tagline || p.specialization || "—"}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {p.is_active ? "Active" : "Inactive"}
                      </span>
                      {p.is_trial_eligible && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">Trial</span>
                      )}
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {p.card_color}
                      </span>
                      {p.link_status === "broken" && (
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold"
                          title="This profile is linked to a deleted user account. Click Edit and pick the correct tutor."
                          data-testid={`broken-link-${p.id}`}
                        >
                          ⚠ Broken link
                        </span>
                      )}
                      {p.link_status === "unlinked" && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          Unlinked
                        </span>
                      )}
                      {p.link_status === "wrong_role" && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                          Wrong role
                        </span>
                      )}
                    </div>
                    {p.linked_user && (
                      <p className="text-[11px] text-slate-400 truncate mt-0.5">
                        🔗 {p.linked_user.name} · {p.linked_user.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-profile-${p.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handlePhotoUpload(p.id, e.target.files[0])}
                      data-testid={`upload-photo-${p.id}`}
                    />
                    <span className="inline-flex w-full items-center justify-center gap-1 border border-slate-300 px-3 py-1.5 rounded-md text-sm hover:bg-slate-50">
                      <Upload className="w-3 h-3" /> Photo
                    </span>
                  </label>
                  <Button size="sm" variant="outline" onClick={() => openAvailability(p)} data-testid={`set-availability-${p.id}`}>
                    <Calendar className="w-3 h-3 mr-1" /> Availability
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(p)} data-testid={`delete-profile-${p.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {profiles.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-500">
              No profiles yet. Click "New Profile" to create one.
            </div>
          )}
        </div>
      )}

      {/* Editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Tutor Profile" : "New Tutor Profile"}</DialogTitle>
            <DialogDescription>
              Fill in the tutor card details that will be shown to students.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Display Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="profile-name" />
              <div>
                <Label>Linked Tutor User</Label>
                <Popover open={tutorPickerOpen} onOpenChange={setTutorPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={tutorPickerOpen}
                      className="w-full justify-between font-normal"
                      data-testid="profile-user-id-trigger"
                      type="button"
                    >
                      <span className="truncate text-left">
                        {form.user_id
                          ? (() => {
                              const u = tutorUsers.find((x) => x.id === form.user_id);
                              return u ? `${u.name} (${u.email})` : "— Unknown user —";
                            })()
                          : "— None (no booking) —"}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[--radix-popover-trigger-width] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Search tutor by name or email..." data-testid="profile-user-id-search" />
                      <CommandList>
                        <CommandEmpty>No tutor found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="none"
                            onSelect={() => {
                              setForm({ ...form, user_id: "" });
                              setTutorPickerOpen(false);
                            }}
                            data-testid="profile-user-id-option-none"
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                !form.user_id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            — None (no booking) —
                          </CommandItem>
                          {tutorUsers.map((u) => (
                            <CommandItem
                              key={u.id}
                              value={`${u.name} ${u.email}`}
                              onSelect={() => {
                                setForm({ ...form, user_id: u.id });
                                setTutorPickerOpen(false);
                              }}
                              data-testid={`profile-user-id-option-${u.id}`}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  form.user_id === u.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{u.name}</span>
                                <span className="text-xs text-slate-500">{u.email}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500 mt-1">
                  Changing this auto-reassigns all existing bookings to the new tutor account.
                </p>
              </div>
            </div>
            <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="profile-tagline" />
            <div>
              <Label>Bio (rich text, supports emojis & line breaks)</Label>
              <Textarea
                rows={6}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                data-testid="profile-bio"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Specialization" value={form.specialization} onChange={(v) => setForm({ ...form, specialization: v })} testid="profile-specialization" />
              <Field
                label="Experience (years)"
                type="number"
                value={form.experience_years || ""}
                onChange={(v) => setForm({ ...form, experience_years: v })}
                testid="profile-exp"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Teaching Style" value={form.teaching_style} onChange={(v) => setForm({ ...form, teaching_style: v })} testid="profile-style" />
              <Field label="Education" value={form.education} onChange={(v) => setForm({ ...form, education: v })} testid="profile-edu" />
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Card Color</Label>
                <select
                  className="w-full border border-slate-300 rounded-md py-2 px-3 text-sm"
                  value={form.card_color}
                  onChange={(e) => setForm({ ...form, card_color: e.target.value })}
                  data-testid="profile-card-color"
                >
                  <option value="blue">Blue</option>
                  <option value="white">White</option>
                </select>
              </div>
              <Field
                label="Display Order"
                type="number"
                value={form.display_order}
                onChange={(v) => setForm({ ...form, display_order: v })}
                testid="profile-order"
              />
              <div className="flex flex-col gap-1">
                <Label>Status</Label>
                <div className="flex items-center gap-3 mt-2">
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      data-testid="profile-active"
                    /> Active
                  </label>
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={form.is_trial_eligible}
                      onChange={(e) => setForm({ ...form, is_trial_eligible: e.target.checked })}
                      data-testid="profile-trial"
                    /> Trial
                  </label>
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Zoom Link" value={form.zoom_link} onChange={(v) => setForm({ ...form, zoom_link: v })} testid="profile-zoom" />
              <Field label="WhatsApp" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} testid="profile-whatsapp" />
              <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="profile-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={savings} data-testid="save-profile-btn">
              {savings ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Availability dialog */}
      <Dialog open={!!availabilityFor} onOpenChange={(o) => !o && setAvailabilityFor(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Weekly Availability — {availabilityFor?.name}</DialogTitle>
            <DialogDescription>
              Add time windows per weekday. Slots are auto-generated in 30-minute increments. Times in Istanbul TZ.
            </DialogDescription>
          </DialogHeader>
          <AvailabilityEditor days={availDays} setDays={setAvailDays} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailabilityFor(null)}>Close</Button>
            <Button onClick={saveAvailability} disabled={savingAvail} data-testid="save-availability-btn">
              {savingAvail ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Availability"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function AvailabilityEditor({ days, setDays }) {
  const updateDay = (idx, field, value) => {
    const next = [...days];
    next[idx] = { ...next[idx], [field]: value };
    setDays(next);
  };
  const removeDay = (idx) => setDays(days.filter((_, i) => i !== idx));
  const addDay = () => setDays([...days, { weekday: 0, start: "18:00", end: "22:00" }]);

  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto">
      {days.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-4">No availability set yet.</p>
      )}
      {days.map((d, idx) => (
        <div key={idx} className="flex items-center gap-2" data-testid={`avail-row-${idx}`}>
          <select
            className="border rounded-md py-1 px-2 text-sm"
            value={d.weekday}
            onChange={(e) => updateDay(idx, "weekday", parseInt(e.target.value))}
          >
            {WEEKDAYS.map((w, i) => (
              <option key={i} value={i}>{w}</option>
            ))}
          </select>
          <Input
            type="time"
            value={d.start}
            onChange={(e) => updateDay(idx, "start", e.target.value)}
            className="w-32"
          />
          <span className="text-slate-400">→</span>
          <Input
            type="time"
            value={d.end}
            onChange={(e) => updateDay(idx, "end", e.target.value)}
            className="w-32"
          />
          <Button size="sm" variant="outline" className="text-red-600" onClick={() => removeDay(idx)}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addDay} data-testid="add-availability-row">
        <Plus className="w-3 h-3 mr-1" /> Add Time Window
      </Button>
    </div>
  );
}
